// src/lib/i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { store } from "@/data/store";
import { setLanguageMap } from "@/data/slices/languageSlice";

/**
 * Initialise i18next **and** populate the Redux language map.
 * Call this once at application start‑up.
 */
export async function initI18n() {
  // -------------------------------------------------------------
  // 1️⃣  Load the list of supported languages from the API
  // -------------------------------------------------------------
  const langs = await fetch("/api/translation/available")
    .then((r) => r.json())
    .catch(() => []); // safe fallback

  // Build two structures we need later:
  //   * `codes` – array of language codes for i18next
  //   * `map`   – object { code: displayName } for the UI
  const codes = langs.map((l) => l.code);
  const map = langs.reduce((acc, l) => {
    acc[l.code] = l.displayName;
    return acc;
  }, {});

  // -------------------------------------------------------------
  // 2️⃣  Store the map in Redux (no window usage)
  // -------------------------------------------------------------
  store.dispatch(setLanguageMap(map));

  // -------------------------------------------------------------
  // 3️⃣  Initialise i18next – it will request /api/translation/{{lng}}
  // -------------------------------------------------------------
  await i18n
    .use(HttpBackend)
    .use(LanguageDetector) // auto‑detect browser language
    .use(initReactI18next)
    .init({
      fallbackLng: "en",
      supportedLngs: codes,
      returnEmptyString: false,
      returnNull: false,

      // important when browser gives en-US but you support only en
      load: "languageOnly",
      cleanCode: true,

      ns: ["translation"],
      defaultNS: "translation",

      backend: {
        loadPath: "/api/translation/{{lng}}",
      },

      detection: {
        // make behavior explicit instead of relying on defaults
        order: ["localStorage", "navigator", "htmlTag", "path", "subdomain"],
        caches: ["localStorage"],

        // normalize browser locale like en-US -> en
        convertDetectedLanguage: (lng) => {
          if (!lng) return lng;
          return lng.split("-")[0].toLowerCase();
        },
      },

      interpolation: { escapeValue: false },
      debug: import.meta.env.DEV,
      react: {
        transSupportBasicHtmlNodes: true,
        transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "span"],
      },
    });

  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng;
  });

  return i18n;
}
